// import withQueryUtils from 'with-query';
import get from 'lodash/get';
import { call } from 'redux-saga/effects';

import { Method } from '.';
import { getFetchHeaders } from './utils';

const hostname = process.env.API_ENDPOINT;

const serializeRequestBody = (body: unknown): string | undefined => {
  if (body == null) {
    return undefined;
  }
  if (typeof body === 'string') {
    return body;
  }
  return JSON.stringify(body);
};

const validateResponse = (response) => {
  if (!response.ok) {
    return Promise.reject(response);
  }
  return response;
};

const readResponseAsJSON = (response) => {
  const json = response.json();
  if (response.status >= 200 && response.status < 400) {
    return Promise.resolve(json);
  }
  return Promise.reject(response);
};

const readResponseAsText = (response) => {
  const text = response.text();
  if (response.status >= 200 && response.status < 400) {
    return Promise.resolve(text);
  }
  return Promise.reject(response);
};

const readResponseAsIMAGE = (response) => {
  const blob = response.blob();

  if (response.status >= 200 && response.status < 400) {
    return Promise.resolve(blob);
  }
  return Promise.reject(response);
};

const handleResponseError = (response) =>
  new Promise((_resolve, reject) => {
    if (response.text) {
      return response
        .text() // response.json() doesn't appear to support sensible error handling of non-JSON
        .then((text) => {
          let jsonData = {};
          try {
            jsonData = JSON.parse(text); // try to do our own parsing of the json here
          } catch (err) {
            // no-op
          }

          // Controlla se l'errore indica un refresh token scaduto
          if (
            jsonData &&
            typeof jsonData === 'object' &&
            'error' in jsonData &&
            jsonData.error === 'invalid_grant' &&
            'error_description' in jsonData &&
            typeof jsonData.error_description === 'string' &&
            jsonData.error_description.includes('Token is not active')
          ) {
            console.log('Rilevato errore refresh token scaduto:', jsonData);
            handleTokenExpired();
          }

          return jsonData;
        })
        .then((packet) => {
          const error = { packet, response };
          return reject(error);
        });
    }
    return response;
  });

// Function to check if the error is due to token expired
const isTokenExpiredError = (response) => {
  return response.status === 401 || response.status === 403;
};

// Function to handle token expired
const handleTokenExpired = () => {
  console.log('Token expired detected in an API call, forcing page refresh');
  // Dispatch a custom event to notify that the token is expired
  window.dispatchEvent(new CustomEvent('token-expired'));
  // Alternatively, we can force a page reload
  setTimeout(() => {
    window.location.reload();
  }, 1000);
};

// Helper function to combine default headers with custom headers
const combineHeaders = (defaultHeaders: Headers, customHeaders: Record<string, string> = {}) => {
  const finalHeaders = new Headers(defaultHeaders);

  // Add custom headers, overriding defaults if needed
  Object.entries(customHeaders).forEach(([key, value]) => {
    finalHeaders.set(key, value);
  });

  return finalHeaders;
};

const fetchWrapper = ({
  url,
  options = { method: Method.GET },
  formData,
  // withQuery,
  authenticated,
  image,
}: {
  url: string;
  options?: any;
  formData?: any;
  withQuery?: any;
  authenticated?: boolean;
  image?: any;
}) => {
  const { method, body, headers: customHeaders } = options;

  if (image) {
    const defaultHeaders = getFetchHeaders({ authenticated, method });
    const finalHeaders = combineHeaders(defaultHeaders, customHeaders);

    return fetch(url.includes('http:') ? url : `${hostname}/${url}`, {
      method,
      body: serializeRequestBody(body) ?? formData ?? undefined,
      headers: finalHeaders,
    })
      .then(validateResponse)
      .then(readResponseAsIMAGE)
      .then((images) => URL.createObjectURL(images))
      .catch((error) => {
        // Gestisce errori di token scaduto anche per le immagini
        if (isTokenExpiredError(error)) {
          handleTokenExpired();
        }
        return handleResponseError(error);
      });
  }
  // if (withQuery)
  //   return fetch(withQueryUtils(url.includes('http:') ? url : `${hostname}/${url}`, body), {
  //     method,
  //     headers: getFetchHeaders({ url, headers, authenticated, method }),
  //   })
  //     .then(validateResponse)
  //     .then(readResponseAsJSON)
  //     .catch(handleResponseError);

  const defaultHeaders = getFetchHeaders({ authenticated, method });
  const finalHeaders = combineHeaders(defaultHeaders, customHeaders);

  return fetch(url.includes('http:') ? url : `${hostname}/${url}`, {
    method,
    body: serializeRequestBody(body) ?? formData ?? undefined,
    headers: finalHeaders,
  })
    .then(validateResponse)
    .then((response) => {
      if (method !== Method.DELETE) {
        // If the Accept header is text/csv, read as text, otherwise read as JSON
        if (customHeaders && customHeaders['Accept'] === 'text/csv') {
          return readResponseAsText(response);
        }
        return readResponseAsJSON(response);
      }
      return Promise.resolve(response);
    })
    .catch((error) => {
      // Handle token expired errors
      if (isTokenExpiredError(error)) {
        handleTokenExpired();
      }
      return handleResponseError(error);
    });
};

interface FetchAPIOptions {
  method?: Method;
  params?: any;
  authenticated?: boolean;
  formData?: any;
  withQuery?: any;
  image?: any;
  customHeaders?: Record<string, string>;
}

function* fetchAPI(
  url: string,
  options: FetchAPIOptions = {
    method: Method.GET,
    authenticated: true,
  },
) {
  const { method, params, authenticated, formData, withQuery, image, customHeaders } = options;

  try {
    const response = yield call(fetchWrapper, {
      url,
      options: {
        method,
        headers: customHeaders || {},
        body: params ?? undefined,
      },
      formData,
      withQuery,
      authenticated: authenticated ?? true,
      image,
    });

    return response;
  } catch (res) {
    if (res === '500' || get(res, 'response.status') === 500) {
      // yield put(push('/generic-error'));
      console.log('ERROR 500 >>>>>>>>>>');
    }

    if ([401, 403].includes(get(res, 'response.status'))) {
      console.log('Token expired detected in fetchAPI, handling error...');
      // L'errore è già gestito nel fetchWrapper, ma possiamo aggiungere logica aggiuntiva qui
      // yield put(setRedirectUrl(history.location.pathname));
      // yield put(push(`/auth/sign-up`));
    }

    throw res;
  }
}

export default fetchAPI;
